import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getLinkSuggestions, linkExpenseToRecurring } from '../../api/wallet';
import { getErrorMessage } from '../../utils/errors';
import { formatValue } from '../../utils/utils';

const reasonLabel = (reason) => (reason === 'category' ? 'categoria' : 'descrição');

// Reviews app-proposed links between existing expenses and recurring budget
// items. Everything is pre-selected; the user unchecks what they don't want
// and applies the rest — nothing is linked without confirmation.
const SuggestLinksDialog = ({ open, onClose, onApplied }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    getLinkSuggestions()
      .then((res) => {
        if (!active) return;
        const list = res.data?.Suggestions || [];
        setSuggestions(list);
        setSelected(Object.fromEntries(list.map((s) => [s.CashOutId, true])));
      })
      .catch((err) => active && setError(getErrorMessage(err, 'Não foi possível carregar as sugestões.')))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open]);

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const chosen = suggestions.filter((s) => selected[s.CashOutId]);

  const handleApply = async () => {
    setApplying(true);
    setError('');
    try {
      for (const s of chosen) {
        await linkExpenseToRecurring(s.CashOutId, s.RecurringExpenseId);
      }
      onApplied?.(chosen.length);
      onClose?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao aplicar os vínculos.'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sugerir vínculos</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : suggestions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            Nenhuma sugestão de vínculo encontrada.
          </Typography>
        ) : (
          <List dense>
            {suggestions.map((s) => (
              <ListItem key={s.CashOutId} disableGutters>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={!!selected[s.CashOutId]}
                    onChange={() => toggle(s.CashOutId)}
                    inputProps={{ 'aria-label': `sug-${s.CashOutId}` }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{s.Description}</Typography>
                      <Typography variant="body2" color="text.secondary">→</Typography>
                      <Typography variant="body2" color="primary">{s.RecurringExpenseName}</Typography>
                      <Chip size="small" variant="outlined" label={reasonLabel(s.Reason)} />
                    </Box>
                  }
                  secondary={`${formatValue(Number(s.Value) || 0, s.Currency)} · ${s.DebitDate}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleApply} disabled={applying || chosen.length === 0}>
          Aplicar{chosen.length > 0 ? ` (${chosen.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuggestLinksDialog;
